<template>
  <div class="bank-statement-details">
    <BaseExtractedDetails
      v-if="hasBaseData"
      :sections="baseSections"
      :has-data="hasBaseData"
      :data="normalizedData"
    />

    <div v-if="dailyBalanceRows.length" class="details-section daily-balances-section">
      <h3 class="section-title">Daily Balances</h3>
      <div class="daily-balances-summary">
        <span>{{ dailyBalanceSummary.totalDays }} days</span>
        <span>{{ dailyBalanceSummary.activeDays }} with balance</span>
        <span>Closing: {{ formatBalance(dailyBalanceSummary.closingBalance) }}</span>
      </div>

      <div v-if="dailyBalanceChart.hasData" class="daily-balance-chart">
        <apexchart
          type="area"
          :options="dailyBalanceChart.options"
          :series="dailyBalanceChart.series"
          height="240"
        />
      </div>

      <DailyBalanceCalendar :rows="dailyBalanceRows" />
    </div>
  </div>
</template>

<script>
import BaseExtractedDetails from './BaseExtractedDetails.vue'
import DailyBalanceCalendar from './DailyBalanceCalendar.vue'

export default {
  name: 'BankStatementDetails',
  components: {
    BaseExtractedDetails,
    DailyBalanceCalendar
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    dailyBalances: {
      type: Object,
      default: null
    }
  },
  computed: {
    primaryAccount() {
      if (!this.data) return {};

      if (Array.isArray(this.data.account_details) && this.data.account_details.length > 0) {
        return this.data.account_details[0];
      }

      return this.data;
    },

    normalizedData() {
      const account = this.primaryAccount;

      return {
        account_holder_name: account.account_holder_name,
        account_number: account.account_number,
        bank_name: account.bank_name,
        branch_name: account.branch_name,
        account_type: account.account_type,
        currency: account.currency,
        iban: account.iban,
        swift_code: account.swift_code,
        transaction_count: this.getTransactionCount()
      };
    },

    dailyBalanceRows() {
      const balancesSource = this.dailyBalances || this.data?.daily_balances;
      if (!balancesSource || typeof balancesSource !== 'object') {
        return [];
      }

      const accountKey = this.primaryAccount?.iban || Object.keys(balancesSource)[0];
      const accountBalances = accountKey ? balancesSource[accountKey] : null;

      if (!accountBalances || typeof accountBalances !== 'object') {
        return [];
      }

      return Object.entries(accountBalances)
        .map(([date, balance]) => ({
          date,
          amount: Number(balance)
        }))
        .filter((row) => Number.isFinite(row.amount))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    dailyBalanceSummary() {
      const rows = this.dailyBalanceRows;

      return {
        totalDays: rows.length,
        activeDays: rows.filter((row) => row.amount !== 0).length,
        closingBalance: rows.length ? rows[rows.length - 1].amount : 0
      };
    },

    dailyBalanceChart() {
      const rows = this.dailyBalanceRows;

      if (!rows.length) {
        return { hasData: false };
      }

      return {
        hasData: true,
        series: [{
          name: 'Balance',
          data: rows.map((row) => ({
            x: new Date(row.date).getTime(),
            y: row.amount
          }))
        }],
        options: {
          chart: {
            id: 'daily-balance-chart',
            toolbar: { show: false },
            zoom: { enabled: false }
          },
          colors: ['#166534'],
          fill: {
            type: 'gradient',
            gradient: {
              shadeIntensity: 1,
              opacityFrom: 0.35,
              opacityTo: 0.05,
              stops: [0, 100]
            }
          },
          stroke: { curve: 'smooth', width: 2 },
          dataLabels: { enabled: false },
          xaxis: {
            type: 'datetime',
            labels: {
              style: { fontSize: '11px', colors: '#64748b' },
              datetimeFormatter: {
                year: 'yyyy',
                month: 'MMM',
                day: 'dd MMM'
              }
            },
            axisBorder: { show: false },
            axisTicks: { show: false }
          },
          yaxis: {
            labels: {
              formatter: (val) => {
                if (Math.abs(val) >= 1e9) return `${(val / 1e6).toFixed(1).replace(/\.0$/, '')}B`;
                if (Math.abs(val) >= 1e6) return `${(val / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
                if (Math.abs(val) >= 1e3) return `${(val / 1e3).toFixed(1).replace(/\.0$/, '')}K`;

                return val.toFixed(0);
              },
              style: { fontSize: '11px', colors: '#64748b' }
            }
          },
          grid: {
            strokeDashArray: 4,
            borderColor: '#e2e8f0'
          },
          tooltip: {
            theme: 'light',
            x: {
              format: 'dd MMM yyyy'
            },
            y: {
              formatter: (val) => this.formatBalance(val)
            }
          }
        }
      };
    },

    hasBaseData() {
      const hasAccountDetails = Array.isArray(this.data?.account_details) && this.data.account_details.length > 0;
      const hasTransactions = this.getTransactionCount() > 0;

      return hasAccountDetails || hasTransactions;
    },

    hasData() {
      return this.hasBaseData || this.dailyBalanceRows.length > 0;
    },

    baseSections() {
      return [
        {
          title: 'Account Details',
          icon: 'las la-university',
          type: 'info-grid',
          fields: [
            { key: 'account_holder_name', label: 'Account Holder', format: 'text' },
            { key: 'account_number', label: 'Account Number', format: 'text' },
            { key: 'bank_name', label: 'Bank Name', format: 'text' },
            { key: 'branch_name', label: 'Branch', format: 'text' },
            { key: 'account_type', label: 'Account Type', format: 'text' },
            { key: 'currency', label: 'Currency', format: 'text' },
            { key: 'iban', label: 'IBAN', format: 'text' },
            { key: 'swift_code', label: 'SWIFT Code', format: 'text' }
          ]
        },
        {
          title: 'Transactions',
          icon: 'las la-list',
          type: 'summary-grid',
          cards: [
            { key: 'transaction_count', label: 'Number of Transactions', format: 'text' }
          ]
        }
      ];
    }
  },
  methods: {
    getTransactionCount() {
      if (!this.data) return 0;

      if (Array.isArray(this.data.transactions)) {
        return this.data.transactions.length;
      }

      if (this.data.transactions_by_account && typeof this.data.transactions_by_account === 'object') {
        return Object.values(this.data.transactions_by_account).reduce(
          (total, accountTransactions) => total + (Array.isArray(accountTransactions) ? accountTransactions.length : 0),
          0
        );
      }

      return 0;
    },

    formatBalance(balance) {
      const amount = Number(balance);
      if (!Number.isFinite(amount)) return 'N/A';

      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../index.scss';

.bank-statement-details {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.details-section {
  background: #ffffff;
  padding: 1rem;
  margin-bottom: 0.5rem;

  .section-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.75rem 0;
    font-family: $brand-font-medium;
  }
}

.daily-balances-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: #64748b;
  font-size: 0.88rem;
  font-family: $brand-font-regular;

  span {
    padding: 0;
  }
}

.daily-balance-chart {
  margin-bottom: 1.5rem;
}
</style>
